import { HttpHeaders } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { EffectiveDateStateStructure } from '../../../shared/components/configs/interfaces/effective-date.state-structure.interface';
import { ZipCountyStateStructure } from '../../../shared/interfaces/zipCountyStateStructure.interface';
import { PlanAttributes, SummaryView } from '../../interfaces/plan-attributes.interface';
import { Product } from '../../interfaces/product.interface';
import { GoogleMapApiService, LatLng } from 'src/app/shared/services/google-map-api.service';
import { ContentStorageService } from '@ptl/global-components';
import { Subscription } from 'rxjs';
import { CombinedMapEntries, ListOfLinksItem } from './interfaces/additional-plan-links.interface';
import { additionalPlanLinksContentKey } from './enums/additional-plan-links.enum';
import { API_ENDPOINTS} from '../../../../shared/constants/api-endpoints.constant';

@Component({
    selector: 'app-additional-plan-links',
    templateUrl: './additional-plan-links.component.html',
    styleUrls: ['./additional-plan-links.component.scss'],
    standalone: false
})
export class AdditionalPlanLinksComponent implements OnInit {
  
  @Input() planData: Product<PlanAttributes, SummaryView>;

  additionalLinks: any[] = null
  miscConfigsContentStorageSubscription: Subscription;
  linksContentStorageSubscription: Subscription;
  hideContent: boolean = true;

  constructor(
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService,
    private reducerUtilityService: ReducerUtilityService,
    private googleMapApiService: GoogleMapApiService,
    private contentStorageService: ContentStorageService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  async ngOnInit() {
    let curBrandCode = this.brandService.getCurrentBrand().cookieBrandCode?.toUpperCase();
    let additionalPlanLinksContent: CombinedMapEntries;
      // Setting the links to empty will hide all links if there is no specific configs
    this.linksContentStorageSubscription = this.contentStorageService.contentChanged$.subscribe(async () => {
      additionalPlanLinksContent = this.contentStorageService.getContentMap(additionalPlanLinksContentKey);
      if (additionalPlanLinksContent?.size > 0) {
        this.hideContent = additionalPlanLinksContent.get("hideContent") ?? true;

        let planLinks = additionalPlanLinksContent.get("listOfLinks");
        if (planLinks?.size) {
          this.generateAdditionalLinks(curBrandCode, planLinks);
        }
      }
    })
  }

  async generateAdditionalLinks(curBrandCode: string, content: Map<string, ListOfLinksItem>) {
    for (const id of content.keys()) {
      const hyperlinkText = content.get(id)[0];
      const authoredLink = content.get(id)[1];

      const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
      const effectiveDateYear = effectiveDateState?.effectiveDateYear;

      let tempUrl: any = authoredLink; 
      let parsedUrl: any = authoredLink;

      switch (true) {
        case id.includes('sbc'):
          if (curBrandCode === 'NDBCBS' || curBrandCode === 'MINCR') {
            parsedUrl= await this.getSbcForCmcrstService();
          } else if(curBrandCode === 'WYBCBS') {
            const blobResponse = await this.generatecmodSBCLink();
            if (blobResponse instanceof Blob) {
              parsedUrl = this.createBlobUrl(blobResponse);
            } else {
              parsedUrl = null;
            }
          } else {
            parsedUrl = this.generateSBCLink();
          }
          break;
        case id.includes('brochure'):
          parsedUrl = this.generatePlanBrochureLink(authoredLink);
          break;
        case id.includes('care'):
        tempUrl = authoredLink;
        const latLng = await this.getLatLng()
        const providedPlanId = this.planData.attributes.PROVIDER_PLAN_ID;
        let providerPlanIdFormatted: string;
        let providerPlanNetworkId: string;
        switch (providedPlanId) {
          case 'BLUE PLUS METRO MN':
            providerPlanIdFormatted = 'MetroMNNetwork';
            providerPlanNetworkId = '291';
            break;
          case 'BLUE PLUS MINNESOTA VALUE':
            providerPlanIdFormatted = 'MinnesotaValueNetwork';
            providerPlanNetworkId = '341';
            break;
          case 'BLUE PLUS SOUTHEAST MN':
            providerPlanIdFormatted = 'SoutheastMNNetwork';
            providerPlanNetworkId = '290';
            break;
          default:
            break;
      }
        parsedUrl = tempUrl?.replace('{networkId}', `${providerPlanNetworkId}`).replace('{productId}', `${providerPlanIdFormatted}`).replace('{coordinates}',`${latLng.lat},${latLng.lng}`);
      }

      const displayHyperlink = {
				"description": `${hyperlinkText}`,
				"url": `${parsedUrl}`
			}

      if (!this.additionalLinks) {
        this.additionalLinks = [];
      }
      this.additionalLinks.push(displayHyperlink);
    }
  }
  generateSBCLink() {
    /**** LEGACY PATH, to be depricated
     * EX:   https://shopdenv7.highmark.com/content/sbcs/2019/WPA/Individual/I_38949PA0080008-00_20190101_SBC.pdf
     * 
     * New Path:
     * https://shop.highmark.com/content/dam/highmark/en/healthco/shopx/plan-documents/2022/sbcs/wpa/individual/I_33709PA0870002-00_20220101_SBC.pdf
     ****/

    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getComponentStates().ZipCounty;
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    const hiosId = this.planData.attributes.HIOS_ID;
    const region = zipCountyState.county.region.toLowerCase();
    const effectiveDateYear = effectiveDateState?.effectiveDateYear;
    const effectiveDateForSBCFilename = effectiveDateYear + "0101";
    
    let url = `/content/dam/highmark/en/healthco/shopx/plan-documents/${effectiveDateYear}/sbcs/${region}/individual/I_${hiosId}_${effectiveDateForSBCFilename}_SBC.pdf`;

    return url;
  }

  async generatecmodSBCLink(): Promise<Blob | null> {
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
    const effectiveDateYear = effectiveDateState?.effectiveDateYear;
    // Pad HIOS_ID with two zeros
    const paddedHiosId = this.planData.attributes.HIOS_ID.padEnd(16, '0');
    const payload = {
      "payload": {
        "region": "WY",
        "year": `${effectiveDateYear}`,
        "hiosID": paddedHiosId,
        "docType": "SBC"
      }
    };
    
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      strategyConfigs: { forceModernUrl: true },
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: payload,
      url: API_ENDPOINTS.CMOD.GET_DOCUMENTS,
      responseType: 'blob'
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    try {
      const response = await this.httpClient.makeRequest(requestConfig) as Blob;
      return response;
    } catch (e) {
      console.error('Failed to fetch SBC blob', e);
      return null;
    }
    
  } 

  generatePlanBrochureLink(inputLink) {
    /****
     * EX: "https://shop.highmark.com/content/HCR/Highmark/resources/resources/2022_ACA_Brochure_WV.pdf"
     ****/

    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getComponentStates().ZipCounty;
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    const region = zipCountyState.county.region;
    const effectiveDateYear = effectiveDateState?.effectiveDateYear;

    let url = inputLink.replace("{region}", region.toLowerCase());

    let fileName = `${region.toUpperCase()}_${effectiveDateYear}_ACA_Brochure`
    url = url.replace("{region_year}", fileName);

    return url;
  }

  getSbcForCmcrstService() {
    // var mfPartNumber = domAttr.get(evt.target, 'data-part');

    const superProductId = this.planData.attributes.SUPER_PRODUCT_ID;

    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    const sbcPostData = {
      "baseMessage": { "headerVersion": "1", "returnCode": null, "returnCodeDescription": null, "statusMsgs": null, "payloadVersion": "1" },
      "payload": {
        "productType": "I",
        "effectiveDate": `01/01/${effectiveDateState.effectiveDateYear}`,
        "productIds": [superProductId]
      }
    };

    const host = window.location.hostname;
    let env = '';
    if (host.indexOf('env') >= 0) 
        env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));
      
    const requestConfigImplementation = new RequestConfigImplementation({
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.DOCUMENT_INFO.NAS_SBC_DOC_SEARCH, {}),
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        'x-hmhs-test-backend': `wasinter${env}.highmark.com`
      }),
      POST_OR_PUT_PAYLOAD: sbcPostData,
    });
  
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfigImplementation).then(async (sbcData: any) => {
      const fileName =
        sbcData.payload.products[0].NonErisaSbcDoc.fileName;
        
      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.GET,
        strategyConfigs: { forceModernUrl: true },
        headers: new HttpHeaders({
          'x-hmhs-test-backend': `wasinter${env}.highmark.com`
        }),
        url: this.axwayTokenFetcherService.buildApiUrl(`${API_ENDPOINTS.DOCUMENT_INFO.NAS_SBC_DOC_RETRIEVAL}/${fileName}`, {}),
        responseType: 'blob',
        accept: 'application/pdf'
      });
      

      try {
          this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
          const blob = await this.httpClient.makeRequest(requestConfig) as Blob;
          if (blob instanceof Blob) {
            return this.createBlobUrl(blob);
          } else {
            return null; // Or handle other non-Blob responses as needed
          } 
        } catch (e) {
          console.error('Failed to fetch SBC blob', e);
          return null;
        }
      // }
    }).catch(() => {
      console.log('Sorry, no SBC Document found for this plan.');
    });
  }

  async getLatLng(): Promise<{ lat: number; lng: number }> {

    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');

    const zipCode = zipCountyState?.zipCode;
    if (zipCode) {
      try {
        const googleMapsLatLngLookupResponse: LatLng = await this.googleMapApiService.getLatLngFromFormattedZipCode(zipCode).toPromise();

        return {
          lat: googleMapsLatLngLookupResponse.lat,
          lng: googleMapsLatLngLookupResponse.lng
        };
      } catch (e) {
        console.error('Failed to retrieved LAT and LNG from gmaps');
        return {
          lat: 44.9951597271008,
          lng: -93.30759900849817
        };
      }
    }
  }

  private createBlobUrl(blob: Blob): string {
    const blobObj = new Blob([blob], { type: "application/pdf" });
    return URL.createObjectURL(blobObj);
  }
}